/* global BigInt */
// DPGF cell parsing only: no editor mutation, persistence or audit here.
export const readDpgfNumber = (raw) => {
  if (raw === undefined) return { state: 'absent', raw };
  if (raw === null || (typeof raw === 'string' && !raw.trim())) return { state: 'empty', raw };
  if (!['number', 'string'].includes(typeof raw)) return { state: 'invalid', raw };
  const input = String(raw).replace(/\s/g, '').replace(',', '.');
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(input)
    || !Number.isFinite(Number(input))) return { state: 'invalid', raw };
  const [mantissa, exponent = '0'] = input.toLowerCase().split('e');
  // Bound expansion of deliberately excessive exponents, including underflow.
  if (Math.abs(Number(exponent)) > 308) return { state: 'invalid', raw };
  const negative = mantissa.startsWith('-');
  const [integer, fraction = ''] = mantissa.replace(/^[+-]/, '').split('.');
  const digits = integer + fraction;
  const point = integer.length + Number(exponent);
  const expanded = point <= 0 ? `0.${'0'.repeat(-point)}${digits}`
    : point >= digits.length ? digits + '0'.repeat(point - digits.length)
      : `${digits.slice(0, point)}.${digits.slice(point)}`;
  const [whole, tail = ''] = expanded.split('.');
  const canonical = (whole.replace(/^0+(?=\d)/, '') || '0')
    + (tail.replace(/0+$/, '') ? `.${tail.replace(/0+$/, '')}` : '');
  return { state: 'valid', raw, decimal: `${negative && canonical !== '0' ? '-' : ''}${canonical}` };
};

// Exact decimal checks at the SQL boundary, not a replacement financial engine.
const roundRatio = (numerator, denominator) => (numerator + denominator / 2n) / denominator;
const atScale = (cell, digits) => {
  const negative = cell.decimal.startsWith('-');
  const [integer, fraction = ''] = cell.decimal.replace('-', '').split('.');
  const magnitude = BigInt(integer + fraction);
  const scaled = roundRatio(magnitude * 10n ** BigInt(digits), 10n ** BigInt(fraction.length));
  return negative ? -scaled : scaled;
};
const fractionDigits = (cell) => (cell.decimal.split('.')[1] || '').length;
const MAX_DECIMAL_14 = 99999999999999n;

export const normalizeDpgfLineValues = ({ quantity, unitPrice, total, rowNumber }) => {
  const source = {
    origin: 'dpgf', rowNumber,
    quantity: readDpgfNumber(quantity), unitPrice: readDpgfNumber(unitPrice), total: readDpgfNumber(total),
  };
  const diagnostics = [];
  const issue = (field, code, message, severity = 'error') => diagnostics.push({ field, code, message, severity });
  const labels = { quantity: 'Quantité', unitPrice: 'Prix unitaire HT', total: 'Total HT' };
  for (const field of Object.keys(labels)) {
    const cell = source[field];
    if (cell.state === 'invalid') issue(field, 'DPGF_NUMBER_INVALID', `${labels[field]} non numérique.`);
    if (cell.state === 'valid' && cell.decimal.startsWith('-')) {
      issue(field, 'DPGF_NEGATIVE_VALUE', `${labels[field]} négatif : ligne non importable.`);
    }
  }
  if (['absent', 'empty'].includes(source.quantity.state)) {
    issue('quantity', 'DPGF_QUANTITY_REQUIRED', `Quantité ${source.quantity.state === 'absent' ? 'absente' : 'vide'} : renseignez-la explicitement.`);
  }
  if (source.quantity.state === 'valid' && fractionDigits(source.quantity) > 4) {
    issue('quantity', 'DPGF_QUANTITY_PRECISION', 'La quantité dépasse la précision persistée de quatre décimales.');
  }
  const hasTotal = source.total.state === 'valid';
  const hasUnit = source.unitPrice.state === 'valid';
  if (!hasTotal && hasUnit && fractionDigits(source.unitPrice) > 4) {
    issue('unitPrice', 'DPGF_UNIT_PRICE_PRECISION', 'Le prix unitaire dépasse la précision persistée de quatre décimales.');
  }
  for (const [field, digits] of [['quantity', 4], ['unitPrice', 4], ['total', 2]]) {
    // A source PU overridden by a total is diagnostic data, not a persisted PU.
    if (field === 'unitPrice' && hasTotal) continue;
    if (source[field].state === 'valid' && atScale(source[field], digits) > MAX_DECIMAL_14) {
      issue(field, 'DPGF_DECIMAL_RANGE', `${labels[field]} dépasse la capacité DECIMAL(14,${digits}).`);
    }
  }
  if (hasTotal && source.quantity.state === 'valid' && atScale(source.quantity, 4) <= 0n) {
    issue('quantity', 'DPGF_TOTAL_REQUIRES_POSITIVE_QUANTITY', 'Un total HT nécessite une quantité strictement positive.');
  }
  const rejected = () => ({ importable: false, source, diagnostics });
  if (diagnostics.some((item) => item.severity === 'error')) return rejected();

  const quantity4 = atScale(source.quantity, 4);
  let price4 = 0n;
  let total2 = null;
  if (hasTotal) {
    total2 = atScale(source.total, 2);
    // cents / quantity(1e-4) -> PU(1e-4), rounded half up.
    price4 = roundRatio(total2 * 1000000n, quantity4);
    if (price4 > MAX_DECIMAL_14) {
      issue('unitPrice', 'DPGF_DECIMAL_RANGE', 'Le prix unitaire dérivé dépasse la capacité DECIMAL(14,4).');
    } else if (roundRatio(price4 * quantity4, 1000000n) !== total2) {
      issue('total', 'DPGF_TOTAL_NOT_REPRESENTABLE', 'Le total HT ne peut pas être conservé au centime avec un prix unitaire à quatre décimales.');
    }
    if (fractionDigits(source.total) > 2) {
      issue('total', 'DPGF_TOTAL_ROUNDED', 'Le total HT source est arrondi au centime (demi supérieur).', 'warning');
    }
    if (hasUnit) {
      // Compare in cents using the complete source PU, before SQL rounding.
      const [integer, fraction = ''] = source.unitPrice.decimal.split('.');
      const sourceTotal = roundRatio(BigInt(integer + fraction) * quantity4, 100n * 10n ** BigInt(fraction.length));
      if (sourceTotal !== total2) {
        issue('unitPrice', 'DPGF_PRICE_CONTRADICTION', 'Le prix unitaire source contredit le total HT ; le total reste prioritaire.', 'warning');
      }
    }
  } else if (hasUnit) {
    price4 = atScale(source.unitPrice, 4);
    total2 = roundRatio(price4 * quantity4, 1000000n);
    if (total2 > MAX_DECIMAL_14) issue('total', 'DPGF_DECIMAL_RANGE', 'Le total dérivé dépasse la capacité DECIMAL(14,2).');
  }
  if (diagnostics.some((item) => item.severity === 'error')) return rejected();
  const forced = hasTotal || hasUnit;
  return {
    importable: true, diagnostics,
    quantity: Number(quantity4) / 10000,
    unitPrice: Number(price4) / 10000,
    sellingPriceMode: forced ? 'forced' : 'calculated',
    forcedUnitSellingPrice: forced ? Number(price4) / 10000 : null,
    ...(forced ? { forceReason: 'Import DPGF' } : {}),
    source: { ...source, priceBasis: hasTotal ? 'total' : hasUnit ? 'unitPrice' : 'automatic',
      retainedUnitPrice: forced ? Number(price4) / 10000 : null,
      retainedTotalHT: total2 === null ? null : Number(total2) / 100 },
  };
};
